import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ICreateAccountResult } from '@app/auth/models/create-account-result.model';
import { CreateAccountStatus } from '@app/auth/models/create-account-status.enum';
import { CustomerRegistrationHttpService } from '@app/auth/services/customer-registration-http.service';
import { ThemeService } from '@app/salon-details/services/theme.service';
import { RegistrationFormGroupModel } from '@app/shared/models/registration-form-group.model';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { DialogsService } from '@app/shared/services/dialogs/dialogs.service';
import { ISeoTagType } from '@app/shared/services/seo/seo-tag-type.enum';
import { SEOService } from '@app/shared/services/seo/seo.service';
import { inOutAnimation } from '@app/shared/utils';
import { TranslateModule } from '@ngx-translate/core';
import { User } from 'oidc-client';
import { Subscription, Observable, NEVER, catchError, switchMap, tap } from 'rxjs';
import { RegistrationFormSmartComponent } from './registration-form/registration-form.smart.component';
import { CustomerRegistrationFormService } from '../services/customer-registration-form.service';

@Component({
  selector: 'app-customer-registration',
  templateUrl: './customer-registration.smart.component.html',
  styleUrls: ['./customer-registration.smart.component.scss'],
  animations: [inOutAnimation],
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule, RegistrationFormSmartComponent
  ]
})
export class CustomerRegistrationSmartComponent implements OnInit, OnDestroy {

  public registrationForm: RegistrationFormGroupModel;
  public isFormSubmitted: boolean;

  private signInRedirectSubscription: Subscription;
  private returnUrl: string;
  private readonly themeService = inject(ThemeService);
  private readonly customerRegistrationFormService = inject(CustomerRegistrationFormService);
  private readonly seoService = inject(SEOService);
  private readonly registrationService = inject(CustomerRegistrationHttpService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogsService = inject(DialogsService);

  constructor() {
    this.seoService.setSEO([
      {
        tagType: ISeoTagType.Title,
        i18nKey: 'AUTH.CREATE-ACCOUNT.META-TITLE'
      },
      {
        tagType: ISeoTagType.Description,
        i18nKey: 'AUTH.CREATE-ACCOUNT.META-DESCRIPTION'
      }
    ]);
  }

  ngOnInit() {
    this.themeService.useDefaultTheme();
    this.redirectIfSignInDetected();
    this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/';
    this.registrationForm = this.customerRegistrationFormService.buildCustomerRegistrationForm();
  }

  ngOnDestroy(): void {
    this.signInRedirectSubscription.unsubscribe();
  }

  public registerCustomer() {
    if (this.registrationForm.invalid) {
      this.isFormSubmitted = true;
    } else {
      this.tryCreateAccount();
    }
  }

  private redirectIfSignInDetected(): void {
    this.signInRedirectSubscription = this.authService.isLoggedIn$()
      .subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.router.navigateByUrl(this.returnUrl);
        }
      });
  }

  private tryCreateAccount(): void {
    this.registrationService.createAccount(this.registrationForm)
      .pipe(
        catchError(() => this.promptGenericError$()),
        switchMap((result: ICreateAccountResult) => {
          switch (result.status) {
            case CreateAccountStatus.Success:
              this.signInRedirectSubscription.unsubscribe();
              return this.finishRegistration$(result.authenticationToken);
            case CreateAccountStatus.UsernameAlreadyInUse:
              return this.promptEmailAlreadyInUse$();
            default:
              return this.promptGenericError$();
          }
        })
      )
      .subscribe();
  }

  private finishRegistration$(authToken: string): Observable<User> {
    return this.authService.signInSilent$(authToken)
      .pipe(
        tap(() => {
          this.router.navigate(['auth', 'create-account-success'], { queryParams: { returnUrl: this.returnUrl } });
        })
      );
  }

  private promptEmailAlreadyInUse$(): Observable<never> {
    return this.showErrorMessageAndCancelStream$('AUTH.CREATE-ACCOUNT-ERROR.USERNAME-ALREADY-IN-USE');
  }

  private promptGenericError$(): Observable<never> {
    return this.showErrorMessageAndCancelStream$('AUTH.CREATE-ACCOUNT-ERROR.GENERIC-ERROR');
  }

  private showErrorMessageAndCancelStream$(messageKey: string): Observable<never> {
    return this.dialogsService.showOkDialog(
      messageKey,
      {
        dissmissable: true,
        showIcon: true,
        iconClass: 'alert-octagon',
        okButtonTextTranslationKey: 'AUTH.CREATE-ACCOUNT-ERROR.CLOSE-MODAL-BUTTON-TEXT'
      })
      .pipe(switchMap(() => NEVER));
  }
}
